import { GridProps } from '@mui/material';
import { FC, useContext } from 'react';
import type { ComponentType, SVGProps } from 'react';
import { Link } from 'react-router-dom';

import { LogoContainerStyled } from './styles/LogoStyles';
import { PlatformAltTexts } from '../../../constants/PlatformAltTexts';
import { TestingAttributes } from '../../../constants/testingAttributes/testIdGenerators';
import { FeatureFlagsContext } from '../../../contexts/FeatureFlagsContext';
import { useDefaultStudentStartPage } from '../../../hooks/useHomepagePath';
import useWhiteLabel from '../../../hooks/useWhiteLabel';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { getCustomRedirectUrl } from '../../../utils/getCustomRedirectUrl';
import { getLogoWhiteLabel } from '../../../utils/getLogoFrom';
import AdvisorLogoIcon from '../../icons/svg/AdvisorLogoIcon';
import OneMentorMenuLogoIcon from '../../icons/svg/OneMentorMenuLogoIcon';

type LogoSource = string | ComponentType<SVGProps<SVGSVGElement>> | undefined;

interface IDashboardLogoProps {
  isLearner?: boolean;
  testAttrs?: TestingAttributes;
}

interface ILogoRenderProps {
  source: LogoSource;
  altText: string;
}

const isStringUrl = (source: LogoSource): source is string => typeof source === 'string' && source !== undefined;
const isComponent = (source: LogoSource): source is ComponentType<SVGProps<SVGSVGElement>> =>
  typeof source === 'function';

const determineLogoSource = (isLearner: boolean, images: any): LogoSource => {
  if (!isLearner) {
    return AdvisorLogoIcon;
  }

  if (images?.customLogo) {
    return getLogoWhiteLabel(images?.customLogo).url;
  }

  return OneMentorMenuLogoIcon;
};

const LogoRenderer: FC<ILogoRenderProps> = ({ source, altText }) => {
  if (isStringUrl(source)) {
    return <img alt={altText} aria-hidden src={source} />;
  }

  if (isComponent(source)) {
    const LogoComponent = source;

    return <LogoComponent aria-hidden />;
  }

  return <AdvisorLogoIcon aria-hidden />;
};

const Logo: FC<GridProps & IDashboardLogoProps> = ({ sx, isLearner = false, testAttrs }) => {
  const { features } = useContext(FeatureFlagsContext);
  const defaultStudentStartPage = useDefaultStudentStartPage();
  const { images } = useWhiteLabel();

  const getRedirectUrl = (): string => {
    const { qsemLearnerCustomRedirectUrl } = features;

    if (qsemLearnerCustomRedirectUrl) {
      return getCustomRedirectUrl(qsemLearnerCustomRedirectUrl);
    }

    return isLearner ? defaultStudentStartPage : PATH_DASHBOARD.admin.pageInsights;
  };

  const logoSource = determineLogoSource(isLearner, images);

  return (
    <LogoContainerStyled sx={{ ...sx }}>
      <Link to={getRedirectUrl()} aria-label={PlatformAltTexts.sidebar.description} {...testAttrs}>
        <LogoRenderer source={logoSource} altText={PlatformAltTexts.sidebar.title} />
      </Link>
    </LogoContainerStyled>
  );
};

export default Logo;
